import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { ArrowLeft, CheckCircle2, Mail, KeyRound, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../services/api';

// 3-step forgot-password flow:
//   1. EMAIL    -- user enters their email, server sends a 6-digit code
//   2. OTP      -- user enters the code, we verify it (no state change yet)
//   3. PASSWORD -- user picks a new password and we call resetPassword
//   4. DONE     -- success state with a "Back to Login" button
//
// We don't push the user to a new route between steps -- everything is in
// one component with internal `step` state. That keeps the URL stable
// (`/forgot-password`) and avoids losing the entered email/OTP on refresh
// being the user's only real failure mode (which is acceptable: they just
// start over).
const STEPS = {
  EMAIL: 'email',
  OTP: 'otp',
  PASSWORD: 'password',
  DONE: 'done',
};

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setStep(STEPS.EMAIL);
    setEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Step 1 -> Step 2: ask the backend to issue an OTP for this email.
  // The server intentionally returns the same generic message regardless
  // of whether the account exists, so we can't reveal that here either.
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      toast.success('If the account exists, a reset code has been sent');
      setStep(STEPS.OTP);
    } catch (error) {
      console.error('Forgot password error:', error);
      // Even on backend errors we don't want to leak whether the email
      // existed -- show a generic failure message and let the user retry.
      toast.error(error.response?.data?.detail || 'Could not send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 -> Step 3: advance to the password step. We deliberately do NOT
  // call /auth/verify-otp here -- the backend marks the OTP row `is_used=true`
  // on a successful verify, which would then make the /auth/reset-password
  // call in step 3 fail (it re-verifies the OTP server-side as the single
  // source of truth). So we just check that the user typed 6 digits and let
  // them proceed; if the code is wrong, they'll find out at the reset step
  // and we'll bounce them back here (with the same email already filled in).
  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setStep(STEPS.PASSWORD);
  };

  // Step 3 -> Step 4: set the new password. The backend re-verifies the OTP
  // here (single source of truth), validates password strength, and updates
  // the user row.
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(email.trim(), otp, newPassword);
      toast.success('Password reset successfully');
      setStep(STEPS.DONE);
    } catch (error) {
      console.error('Reset password error:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Could not reset password. Please try again.');
      // If the backend rejected the OTP (wrong code, expired, or attempts
      // exhausted), bounce the user back to the OTP step so they can re-enter
      // it. We keep the email and clear only the OTP -- if attempts are
      // exhausted they'll have to request a new code via "Use a different
      // email" -> back to step 1, but typo cases recover cleanly.
      if (detail?.toLowerCase().includes('otp')) {
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setStep(STEPS.OTP);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-1">
          <div className="flex justify-center">
            <img
              src="/Billsera.png"
              alt="Billsera"
              className="w-48 h-auto -my-6"
              style={{ maxHeight: '140px' }}
            />
          </div>
          <CardTitle className="text-xl">Reset your password</CardTitle>
          <CardDescription>
            {step === STEPS.EMAIL && 'Enter your email and we will send you a verification code'}
            {step === STEPS.OTP && 'Enter the 6-digit code we sent to your email'}
            {step === STEPS.PASSWORD && 'Choose a new password for your account'}
            {step === STEPS.DONE && 'Your password has been updated'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === STEPS.EMAIL && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fp-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    autoFocus
                    disabled={loading}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send reset code'}
              </Button>
            </form>
          )}

          {step === STEPS.OTP && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div className="space-y-2">
                <Label className="block text-center">Verification code</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(val) => setOtp(val)}
                    disabled={loading}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-xs text-center text-slate-500">
                  Sent to <span className="font-medium text-slate-700">{email}</span>
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={loading || otp.length !== 6}>
                {loading ? 'Verifying...' : 'Continue'}
              </Button>
              <button
                type="button"
                onClick={() => setStep(STEPS.EMAIL)}
                className="w-full text-xs text-slate-500 hover:text-slate-700"
                disabled={loading}
              >
                Use a different email
              </button>
            </form>
          )}

          {step === STEPS.PASSWORD && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fp-new-password">New password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="fp-new-password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    autoFocus
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fp-confirm-password">Confirm new password</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="fp-confirm-password"
                    type="password"
                    placeholder="Re-enter the same password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset password'}
              </Button>
            </form>
          )}

          {step === STEPS.DONE && (
            <div className="space-y-5 text-center">
              <div className="flex justify-center">
                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
              </div>
              <p className="text-sm text-slate-600">
                You can now sign in with your new password.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Back to login
              </Button>
            </div>
          )}

          {step !== STEPS.DONE && (
            <div className="mt-5 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to login
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
